/**
 * What: The left pane of the field picker: each supported candidate field as a shared `BuilderBox` row — the field name
 *   as the title. The value itself is in the right detail pane. The picked row gets the green check-circle. Every supported
 *   field appears in the bounded scrolling list; an info tooltip explains why other fields are hidden.
 * Transport / data-flow: Dumb renderer over the Worker's `field-picker` block. Which row is picked is the caller's.
 */
import { useId } from 'react'
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import { BuilderBox } from './BuilderBox'

type FieldPickerBlock = Extract<BuilderBlock, { kind: 'field-picker' }>
type Row = FieldPickerBlock['rows'][number]

function CompactRow({
  row,
  selected,
  disabled,
  onSelect,
}: {
  row: Row
  selected: boolean
  disabled: boolean
  onSelect: () => void
}) {
  // The concept badge is the only footer mark, so an unmapped field's footer stays empty rather than saying "no concept yet".
  return (
    <BuilderBox
      title={row.label}
      trailing={selected ? <s-icon type="check-circle" tone="success" /> : undefined}
      selected={selected}
      disabled={disabled}
      onSelect={onSelect}
    />
  )
}

export function ReviewFieldsList({
  block,
  selectedId,
  disabled,
  onSelect,
}: {
  block: FieldPickerBlock
  selectedId: string
  disabled: boolean
  onSelect: (rowId: string) => void
}) {
  const hiddenFieldsId = useId().replaceAll(':', '')
  return (
    <s-stack direction="block" gap="small-200">
      <s-text type="strong">{block.fieldsHeading}</s-text>
      <div className="tf-review-fields__list-scroll">
        <s-stack direction="block" gap="small-200">
          {block.rows.map((row) => (
            <CompactRow
              key={row.id}
              row={row}
              selected={row.id === selectedId}
              disabled={disabled}
              onSelect={() => onSelect(row.id)}
            />
          ))}
        </s-stack>
      </div>
      {block.hiddenFields ? (
        <div className="tf-review-fields__hidden-fields">
          <s-text color="subdued">{block.hiddenFields.label}</s-text>
          <s-button
            variant="tertiary"
            icon="info"
            interestFor={hiddenFieldsId}
            accessibilityLabel={block.hiddenFields.accessibilityLabel}
          />
          <s-tooltip id={hiddenFieldsId}>{block.hiddenFields.explanation}</s-tooltip>
        </div>
      ) : null}
    </s-stack>
  )
}
